import axios from "axios";
import { getIdToken } from "firebase/auth";
import { auth } from "../../auth";
import { getActivePatientId } from "../application/portalContext";
import type {
  ConsentDocument,
  NoteReply,
  PatientConsent,
  PatientInvitation,
  PortalAccount,
  PortalMe,
  PortalSession,
  PortalAgreement,
  PatientNote,
  PatientBookingAvailability,
  PortalSlotList,
  PortalBookedSession,
  PortalNextSession,
  PatientPaymentsAvailability,
  PortalPayment,
  PortalWhatsAppPreference,
} from "../domain/types";
import type { PortalMapSummary, PublicSystemicMap } from "../../systemic_map";
import type { PortalMeditation } from "../domain/types";
import type { ConsentCopySummary } from "../../../shared/ui/consent/ConsentCopiesList";

const portalClient = axios.create({
  baseURL: "/api",
  headers: { "Content-Type": "application/json" },
});

portalClient.interceptors.request.use(async (config) => {
  const user = auth.currentUser;
  if (user) {
    const token = await getIdToken(user, false);
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Which record this call acts on. Omitted when none is active: an account
  // holding a single record resolves it server-side and answers exactly as it
  // did before records could be linked.
  const patientId = getActivePatientId();
  if (patientId) {
    config.headers["X-Patient-Context"] = patientId;
  }
  return config;
});

portalClient.interceptors.response.use(
  (r) => r,
  async (error) => {
    const req = error.config;
    if (error.response?.status === 401 && !req._retry) {
      req._retry = true;
      const token = await auth.currentUser?.getIdToken(true);
      req.headers["Authorization"] = `Bearer ${token}`;
      return portalClient(req);
    }
    return Promise.reject(error);
  }
);

export const PortalAPI = {
  // Invitation (public)
  getInvitation: (token: string) =>
    portalClient.get<PatientInvitation>(`/portal/invitations/${token}`),

  acceptInvitation: (token: string) =>
    portalClient.post<PatientInvitation>(`/portal/invitations/${token}/accept`),

  // Me
  getMe: () => portalClient.get<PortalMe>("/portal/me"),

  /** The account and every record it opens. Takes no record context: it is
   *  the call that tells the client which contexts exist. */
  getAccount: () => portalClient.get<PortalAccount>("/portal/accounts/me"),

  /** The patient ending their own portal account. Not an erasure: the
   *  clinical records stay with each treating therapist. */
  deleteAccount: () => portalClient.delete("/portal/accounts/me"),

  // Consent
  getCurrentConsent: () => portalClient.get<ConsentDocument>("/portal/consent/current"),

  // The document itself, rendered by the backend from the stored text. Fetched
  // rather than framed by URL because the portal is behind a bearer token an
  // <iframe> cannot carry.
  getCurrentConsentPdf: () =>
    portalClient.get<Blob>("/portal/consent/current.pdf", { responseType: "blob" }),

  getConsentHistory: () => portalClient.get<PatientConsent[]>("/portal/consent/history"),

  acceptConsent: (documentId: string) =>
    portalClient.post("/portal/consent/accept", { document_id: documentId }),

  signConsent: (documentId: string, signatureImage: string) =>
    portalClient.post("/portal/consent/sign", {
      document_id: documentId,
      signature_image: signatureImage,
    }),

  revokeConsent: () => portalClient.post("/portal/consent/revoke"),

  getConsentUploadUrl: (contentType: string) =>
    portalClient.post<{ upload_url: string; blob_name: string }>("/portal/consent/upload-url", { content_type: contentType }),

  confirmConsentUpload: (blobName: string) =>
    portalClient.post("/portal/consent/confirm-upload", { blob_name: blobName }),

  getConsentDocumentUrl: () =>
    portalClient.get<{ download_url: string }>("/portal/consent/document-url"),

  // Every copy still stored, newest first — signing again adds one, and
  // withdrawing keeps them.
  getConsentCopies: () =>
    portalClient.get<ConsentCopySummary[]>("/portal/consent/documents"),

  getConsentCopyUrl: (copyId: string) =>
    portalClient.get<{ download_url: string }>(`/portal/consent/documents/${copyId}/url`),

  // Sessions
  getSessions: () => portalClient.get<PortalSession[]>("/portal/sessions"),

  getSession: (sessionId: string) =>
    portalClient.get<PortalSession>(`/portal/sessions/${sessionId}`),

  getSessionAgreements: (sessionId: string) =>
    portalClient.get<PortalAgreement[]>(`/portal/sessions/${sessionId}/agreements`),

  // Agreements
  // Every published agreement the patient can see, in one request. Asking
  // session by session opened the portal with a burst the backend could not
  // absorb.
  getAgreements: () => portalClient.get<PortalAgreement[]>("/portal/agreements"),

  updateAgreement: (agreementId: string, data: { status?: string; comment?: string }) =>
    portalClient.patch<{ id: string; status: string | null; patient_comment: string | null }>(
      `/portal/agreements/${agreementId}`,
      data
    ),

  // Notes
  getNotes: (page = 1) =>
    portalClient.get<{ items: PatientNote[]; total: number; page: number; pages: number }>(`/portal/notes?page=${page}`),

  createNote: (body: string, title?: string, sharedWithTherapist?: boolean, targetSessionId?: string, noteDate?: string) =>
    portalClient.post<PatientNote>("/portal/notes", {
      body,
      title: title ?? null,
      shared_with_therapist: sharedWithTherapist ?? false,
      target_session_id: targetSessionId,
      note_date: noteDate ?? null,
    }),

  updateNote: (noteId: string, body: string, title?: string, sharedWithTherapist?: boolean, noteDate?: string) =>
    portalClient.patch<PatientNote>(`/portal/notes/${noteId}`, {
      body,
      title: title ?? null,
      shared_with_therapist: sharedWithTherapist ?? false,
      note_date: noteDate ?? null,
    }),

  deleteNote: (noteId: string) => portalClient.delete(`/portal/notes/${noteId}`),

  getNoteReplies: (noteId: string) =>
    portalClient.get<NoteReply[]>(`/portal/notes/${noteId}/replies`),

  createNoteReply: (noteId: string, body: string) =>
    portalClient.post<NoteReply>(`/portal/notes/${noteId}/replies`, { body }),

  // GDPR Export
  downloadExport: () =>
    portalClient.get("/portal/export", { responseType: "blob" }),

  // --- Booking (Scheduler tool) ------------------------------------------
  // Slot instants are UTC; they are rendered in the therapist's timezone,
  // which comes back with them.
  getBookingAvailability: () =>
    portalClient.get<PatientBookingAvailability>("/portal/booking/availability"),

  /** Null when nothing is coming, or when the therapist has no Scheduler. */
  getNextSession: () =>
    portalClient.get<PortalNextSession | null>("/portal/booking/sessions/next"),

  getBookingSlots: (date: string) =>
    portalClient.get<PortalSlotList>("/portal/booking/slots", {
      params: { date },
    }),

  bookSession: (start: string) =>
    portalClient.post<PortalBookedSession>("/portal/booking/sessions", { start }),

  /** Cancel the patient's own appointment. 409 carries why it was refused. */
  cancelSession: (sessionId: string) =>
    portalClient.post<void>(`/portal/booking/sessions/${sessionId}/cancel`),

  // --- Payments -----------------------------------------------------------
  // Whether to render the section at all is the server's call, not a guess
  // from anything the portal already knows.
  getPaymentsAvailability: () =>
    portalClient.get<PatientPaymentsAvailability>("/portal/payments/availability"),

  getPayments: () => portalClient.get<PortalPayment[]>("/portal/payments"),

  // One switch for every reminder, on a path that names no tool: consent is
  // the patient's own, and they must be able to see and withdraw it whether
  // or not their therapist still uses whichever tool prompted it.
  getWhatsAppPreference: () =>
    portalClient.get<PortalWhatsAppPreference>("/portal/reminders/whatsapp"),

  /**
   * The genograms and ecomaps the therapist chose to show this patient.
   *
   * Read-only, and there is no write counterpart here by design: the map is
   * the therapist's clinical record, and the portal is where the family reads
   * it between sessions.
   */
  getSystemicMaps: () =>
    portalClient.get<PortalMapSummary[]>("/portal/systemic-maps"),

  getSystemicMap: (mapId: string) =>
    portalClient.get<PublicSystemicMap>(`/portal/systemic-maps/${mapId}`),

  /**
   * The meditations the therapist handed to this patient, to practise with
   * between sessions.
   *
   * Read and play, nothing else — there is no write counterpart here by
   * design. A 403 means the therapist is not using the meditation tool, which
   * the portal reads as "there is no such section", not as a failure.
   */
  getMeditations: () =>
    portalClient.get<PortalMeditation[]>("/portal/meditations"),

  getMeditation: (meditationId: string) =>
    portalClient.get<PortalMeditation>(`/portal/meditations/${meditationId}`),

  /**
   * The recording itself, through our own API rather than a signed link: the
   * bucket does not answer browsers, and the patient is already entitled to
   * hear what they were given.
   */
  getMeditationAudio: (meditationId: string) =>
    portalClient.get<Blob>(`/portal/meditations/${meditationId}/audio`, {
      responseType: "blob",
    }),

  getMeditationBackgroundAudio: (meditationId: string) =>
    portalClient.get<Blob>(
      `/portal/meditations/${meditationId}/background-audio`,
      { responseType: "blob" }
    ),

  setWhatsAppPreference: (opt_in: boolean) =>
    portalClient.put<PortalWhatsAppPreference>("/portal/reminders/whatsapp", {
      opt_in,
    }),
};
